import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AppState, Platform, type AppStateStatus } from 'react-native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { fetchHospitalitySnapshot, type HospitalitySnapshot } from '../infra/hospitalityRepo';
import { useKitchenRealtime } from '../hooks/useKitchenRealtime';

type KitchenTicketCountContextValue = {
  openCount: number | null;
  /** Snapshot compartido: los alert providers lo consumen sin refetchear. */
  snapshot: HospitalitySnapshot | null;
  refresh: () => Promise<void>;
};

const KitchenTicketCountContext = createContext<KitchenTicketCountContextValue>({
  openCount: null,
  snapshot: null,
  refresh: async () => undefined,
});

export function KitchenTicketCountProvider({ children }: { children: React.ReactNode }) {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const hasKitchen = useHasBusinessCapability('kitchen_tickets');
  const enabled = hasKitchen && Boolean(branchId);
  const [snapshot, setSnapshot] = useState<HospitalitySnapshot | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !branchId) {
      setSnapshot(null);
      return;
    }
    try {
      setSnapshot(await fetchHospitalitySnapshot(branchId));
    } catch {
      setSnapshot(null);
    }
  }, [branchId, enabled]);

  useKitchenRealtime(branchId, refresh, { enabled, alwaysOn: true });

  useEffect(() => {
    if (!enabled) {
      setSnapshot(null);
      return;
    }
    void refresh();
    const onActive = () => {
      void refresh();
    };
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      if (next === 'active') onActive();
    });
    let removeVis: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVis = () => {
        if (document.visibilityState === 'visible') onActive();
      };
      document.addEventListener('visibilitychange', onVis);
      removeVis = () => document.removeEventListener('visibilitychange', onVis);
    }
    const interval = setInterval(onActive, 60_000);
    return () => {
      sub.remove();
      removeVis?.();
      clearInterval(interval);
    };
  }, [enabled, refresh]);

  const openCount = useMemo(() => {
    if (!snapshot) return null;
    return snapshot.kitchenTickets.filter(
      (t) => t.status === 'pending' || t.status === 'preparing' || t.status === 'ready',
    ).length;
  }, [snapshot]);

  const value = useMemo(() => ({ openCount, snapshot, refresh }), [openCount, snapshot, refresh]);

  return (
    <KitchenTicketCountContext.Provider value={value}>{children}</KitchenTicketCountContext.Provider>
  );
}

export function useKitchenTicketCount(): KitchenTicketCountContextValue {
  return useContext(KitchenTicketCountContext);
}
